/* EditorialRatesPanel.jsx — Human Editorial Services Phase 1
 *
 * Admin panel for:
 *  • Per-word rate table  (manuscript_type × stage)
 *  • Urgency multipliers + specialist premium
 *  • Platform / editor revenue split
 *  • Editor roster  (add / edit / remove)
 *
 * Sits under Admin → Human Services → "Rate Table" tab.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, Edit2, Check, X, Info } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const MS_TYPES = ['fiction', 'trade_nf', 'academic'];
const MS_LABELS = { fiction: 'Fiction', trade_nf: 'Trade Non-Fiction', academic: 'Academic / Technical' };
const STAGES = ['proofread', 'copyedit', 'developmental', 'full_pipeline'];
const STAGE_LABELS = {
  proofread: 'Proofread',
  copyedit: 'Copyedit',
  developmental: 'Developmental',
  full_pipeline: 'Full Pipeline (bundle)',
};

const AVAIL_OPTIONS = [
  { value: 'available', label: 'Available',   dot: 'bg-emerald-500' },
  { value: 'busy',      label: 'Busy',        dot: 'bg-amber-500'   },
  { value: 'on_leave',  label: 'On Leave',    dot: 'bg-rose-500'    },
];

// ─── helpers ──────────────────────────────────────────────────────────────────

async function fetchJSON(url) {
  const r = await fetch(url, { credentials: 'include' });
  if (!r.ok) throw new Error((await r.json()).detail || r.statusText);
  return r.json();
}
async function putJSON(url, body) {
  const r = await fetch(url, {
    method: 'PUT', credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error((await r.json()).detail || r.statusText);
  return r.json();
}
async function postJSON(url, body) {
  const r = await fetch(url, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error((await r.json()).detail || r.statusText);
  return r.json();
}
async function deleteResource(url) {
  const r = await fetch(url, { method: 'DELETE', credentials: 'include' });
  if (!r.ok) throw new Error((await r.json()).detail || r.statusText);
  return r.json();
}

// ─── Rate Table tab ───────────────────────────────────────────────────────────

function RateTableTab({ config, onSaved }) {
  const [rates, setRates] = useState(config?.rates || {});
  const [mults, setMults] = useState(config?.multipliers || {});
  const [split, setSplit] = useState(config?.revenue_split || { platform_pct: 20, editor_pct: 80 });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (config) {
      setRates(config.rates || {});
      setMults(config.multipliers || {});
      setSplit(config.revenue_split || { platform_pct: 20, editor_pct: 80 });
    }
  }, [config]);

  const setRate = (msType, stage, val) => {
    setRates(prev => ({
      ...prev,
      [msType]: { ...(prev[msType] || {}), [stage]: val },
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const parsed = {};
      for (const [msType, stages] of Object.entries(rates)) {
        parsed[msType] = {};
        for (const [stage, val] of Object.entries(stages)) {
          const n = parseFloat(val);
          if (isNaN(n) || n <= 0) throw new Error(`Invalid rate for ${msType}.${stage}`);
          parsed[msType][stage] = n;
        }
      }
      const parsedMults = {};
      for (const [k, v] of Object.entries(mults)) {
        const n = parseFloat(v);
        if (isNaN(n)) throw new Error(`Invalid multiplier: ${k}`);
        parsedMults[k] = n;
      }
      const result = await putJSON(`${API}/admin/editorial/rates`, {
        rates: parsed,
        multipliers: parsedMults,
        revenue_split: { platform_pct: Number(split.platform_pct), editor_pct: Number(split.editor_pct) },
      });
      toast.success('Rate table saved.');
      onSaved?.(result);
    } catch (e) {
      toast.error(e.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Per-word rates */}
      <section data-testid="editorial-rate-table">
        <h3 className="mb-3 text-[11.5px] font-semibold uppercase tracking-wider text-muted-foreground">
          Per-word rates (USD)
        </h3>
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="border-b border-border bg-muted/40">
                <th className="px-3 py-2.5 text-left font-semibold text-muted-foreground">Manuscript type</th>
                {STAGES.map(s => (
                  <th key={s} className="px-3 py-2.5 text-center font-semibold text-muted-foreground">{STAGE_LABELS[s]}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {MS_TYPES.map((ms, idx) => (
                <tr key={ms} className={idx % 2 === 0 ? '' : 'bg-muted/20'}>
                  <td className="px-3 py-2 font-medium">{MS_LABELS[ms]}</td>
                  {STAGES.map(stage => (
                    <td key={stage} className="px-3 py-2 text-center">
                      <div className="flex items-center justify-center gap-0.5">
                        <span className="text-muted-foreground">$</span>
                        <input
                          type="number"
                          step="0.001"
                          min="0"
                          value={rates[ms]?.[stage] ?? ''}
                          onChange={e => setRate(ms, stage, e.target.value)}
                          data-testid={`rate-${ms}-${stage}`}
                          className="w-20 rounded border border-border bg-background px-1.5 py-1 text-center text-[12px] focus:outline-none focus:ring-1 focus:ring-primary"
                        />
                        <span className="text-[10px] text-muted-foreground">/w</span>
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-2 text-[10.5px] text-muted-foreground flex items-center gap-1">
          <Info className="h-3 w-3" /> EFA-benchmarked below-market rates. These are charged to authors; editors receive the configured share.
        </p>
      </section>

      {/* Multipliers */}
      <section data-testid="editorial-multipliers">
        <h3 className="mb-3 text-[11.5px] font-semibold uppercase tracking-wider text-muted-foreground">Multipliers</h3>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            { key: 'urgency_standard',   label: 'Standard urgency' },
            { key: 'urgency_expedited',  label: 'Expedited (1–2 wks)' },
            { key: 'urgency_rush',       label: 'Rush (< 1 wk)' },
            { key: 'specialist_premium', label: 'Specialist add-on' },
          ].map(({ key, label }) => (
            <div key={key} className="rounded-md border border-border bg-card p-3">
              <label className="mb-1.5 block text-[10.5px] text-muted-foreground">{label}</label>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={mults[key] ?? ''}
                  onChange={e => setMults(prev => ({ ...prev, [key]: e.target.value }))}
                  data-testid={`mult-${key}`}
                  className="w-full rounded border border-border bg-background px-2 py-1 text-[12px] focus:outline-none focus:ring-1 focus:ring-primary"
                />
                <span className="text-[10px] text-muted-foreground">×</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Revenue split */}
      <section data-testid="editorial-revenue-split">
        <h3 className="mb-3 text-[11.5px] font-semibold uppercase tracking-wider text-muted-foreground">Revenue split</h3>
        <div className="flex items-center gap-4 rounded-md border border-border bg-card p-4">
          <div>
            <label className="mb-1 block text-[10.5px] text-muted-foreground">Editor share (%)</label>
            <input
              type="number" min="0" max="100"
              value={split.editor_pct}
              onChange={e => setSplit(s => ({ platform_pct: 100 - Number(e.target.value), editor_pct: Number(e.target.value) }))}
              data-testid="split-editor-pct"
              className="w-24 rounded border border-border bg-background px-2 py-1 text-center text-[13px] focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
          <div className="text-xl font-bold text-muted-foreground">+</div>
          <div>
            <label className="mb-1 block text-[10.5px] text-muted-foreground">Platform share (%)</label>
            <input
              type="number" min="0" max="100"
              value={split.platform_pct}
              onChange={e => setSplit(s => ({ editor_pct: 100 - Number(e.target.value), platform_pct: Number(e.target.value) }))}
              data-testid="split-platform-pct"
              className="w-24 rounded border border-border bg-background px-2 py-1 text-center text-[13px] focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
          <div className="ml-auto text-[11.5px] text-muted-foreground">
            Must total 100%. Currently: <strong className={(split.editor_pct + split.platform_pct === 100) ? 'text-emerald-600' : 'text-rose-600'}>{split.editor_pct + split.platform_pct}%</strong>
          </div>
        </div>
      </section>

      <button
        type="button"
        onClick={save}
        disabled={saving}
        data-testid="editorial-rates-save-btn"
        className="inline-flex items-center gap-2 rounded-sm bg-primary px-4 py-2 text-[12.5px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
      >
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
        Save rate table
      </button>
    </div>
  );
}

// ─── Editor Roster tab ────────────────────────────────────────────────────────

function EditorRosterTab() {
  const [editors, setEditors] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', specialisms: '', availability: 'available', payout_rate_override: '', notes: '', active: true });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await fetchJSON(`${API}/admin/editorial/editors`);
      setEditors(r);
    } catch (e) {
      toast.error('Failed to load editors: ' + e.message);
      setEditors([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setForm({ name: '', email: '', specialisms: '', availability: 'available', payout_rate_override: '', notes: '', active: true });
    setEditingId(null);
    setFormOpen(true);
  };

  const openEdit = (ed) => {
    setForm({
      name: ed.name, email: ed.email,
      specialisms: (ed.specialisms || []).join(', '),
      availability: ed.availability || 'available',
      payout_rate_override: ed.payout_rate_override != null ? String(ed.payout_rate_override) : '',
      notes: ed.notes || '',
      active: ed.active !== false,
    });
    setEditingId(ed.id);
    setFormOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this editor from the roster?')) return;
    try {
      await deleteResource(`${API}/admin/editorial/editors/${id}`);
      toast.success('Editor removed.');
      await load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error('Name and email are required.');
      return;
    }
    setSaving(true);
    try {
      const body = {
        ...form,
        specialisms: form.specialisms.split(',').map(s => s.trim()).filter(Boolean),
        payout_rate_override: form.payout_rate_override !== '' ? parseFloat(form.payout_rate_override) : null,
      };
      if (editingId) {
        await putJSON(`${API}/admin/editorial/editors/${editingId}`, body);
        toast.success('Editor updated.');
      } else {
        await postJSON(`${API}/admin/editorial/editors`, body);
        toast.success('Editor added to roster.');
      }
      setFormOpen(false);
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!editors) return <div className="flex items-center gap-2 text-[12px] text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading roster…</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-[12px] text-muted-foreground">{editors.length} editor{editors.length !== 1 ? 's' : ''} on roster</p>
        <button
          type="button"
          onClick={openNew}
          data-testid="add-editor-btn"
          className="inline-flex items-center gap-1.5 rounded-sm bg-primary px-3 py-1.5 text-[11.5px] font-semibold text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="h-3.5 w-3.5" /> Add editor
        </button>
      </div>

      {editors.length === 0 ? (
        <p className="rounded-md border border-dashed border-border p-6 text-center text-[12px] text-muted-foreground">
          No editors yet. Add your first editor above.
        </p>
      ) : (
        <div className="space-y-2">
          {editors.map(ed => {
            const av = AVAIL_OPTIONS.find(a => a.value === ed.availability) || AVAIL_OPTIONS[0];
            return (
              <div key={ed.id} className="flex items-start gap-3 rounded-md border border-border bg-card p-3" data-testid={`editor-row-${ed.id}`}>
                <span className={`mt-1.5 h-2 w-2 flex-shrink-0 rounded-full ${av.dot}`} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-[12.5px]">{ed.name}</span>
                    <span className="text-[10.5px] text-muted-foreground">{ed.email}</span>
                    {!ed.active && <span className="rounded-sm bg-rose-100 px-1.5 py-0.5 text-[10px] text-rose-700 dark:bg-rose-900/30 dark:text-rose-400">Inactive</span>}
                  </div>
                  <div className="mt-0.5 flex flex-wrap gap-x-3 text-[11px] text-muted-foreground">
                    <span>{av.label}</span>
                    {(ed.specialisms || []).length > 0 && <span>Specialisms: {ed.specialisms.join(', ')}</span>}
                    {ed.payout_rate_override != null && <span>Payout override: {Math.round(ed.payout_rate_override * 100)}%</span>}
                  </div>
                  {ed.notes && <p className="mt-0.5 text-[10.5px] italic text-muted-foreground">{ed.notes}</p>}
                </div>
                <div className="flex gap-1.5">
                  <button type="button" onClick={() => openEdit(ed)} data-testid={`edit-editor-${ed.id}`} className="rounded p-1 hover:bg-secondary"><Edit2 className="h-3.5 w-3.5 text-muted-foreground" /></button>
                  <button type="button" onClick={() => handleDelete(ed.id)} data-testid={`delete-editor-${ed.id}`} className="rounded p-1 hover:bg-secondary"><Trash2 className="h-3.5 w-3.5 text-rose-500" /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add / Edit form */}
      {formOpen && (
        <div className="rounded-md border border-border bg-card p-4 space-y-3" data-testid="editor-form">
          <h4 className="text-[12.5px] font-semibold">{editingId ? 'Edit editor' : 'Add editor'}</h4>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-[10.5px] text-muted-foreground">Name *</label>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="editor-name" className="w-full rounded border border-border bg-background px-2 py-1.5 text-[12px] focus:outline-none focus:ring-1 focus:ring-primary" />
            </div>
            <div>
              <label className="mb-1 block text-[10.5px] text-muted-foreground">Email *</label>
              <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} data-testid="editor-email" className="w-full rounded border border-border bg-background px-2 py-1.5 text-[12px] focus:outline-none focus:ring-1 focus:ring-primary" />
            </div>
            <div>
              <label className="mb-1 block text-[10.5px] text-muted-foreground">Specialisms (comma-separated)</label>
              <input value={form.specialisms} onChange={e => setForm(f => ({ ...f, specialisms: e.target.value }))} placeholder="fiction, thriller, romance" data-testid="editor-specialisms" className="w-full rounded border border-border bg-background px-2 py-1.5 text-[12px] focus:outline-none focus:ring-1 focus:ring-primary" />
            </div>
            <div>
              <label className="mb-1 block text-[10.5px] text-muted-foreground">Availability</label>
              <select value={form.availability} onChange={e => setForm(f => ({ ...f, availability: e.target.value }))} data-testid="editor-availability" className="w-full rounded border border-border bg-background px-2 py-1.5 text-[12px] focus:outline-none focus:ring-1 focus:ring-primary">
                {AVAIL_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-[10.5px] text-muted-foreground">Payout override (e.g. 0.85 for 85%)</label>
              <input type="number" step="0.01" min="0" max="1" value={form.payout_rate_override} onChange={e => setForm(f => ({ ...f, payout_rate_override: e.target.value }))} placeholder="Leave blank to use default split" data-testid="editor-payout-override" className="w-full rounded border border-border bg-background px-2 py-1.5 text-[12px] focus:outline-none focus:ring-1 focus:ring-primary" />
            </div>
            <div>
              <label className="mb-1 block text-[10.5px] text-muted-foreground">Notes</label>
              <input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} data-testid="editor-notes" className="w-full rounded border border-border bg-background px-2 py-1.5 text-[12px] focus:outline-none focus:ring-1 focus:ring-primary" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-[12px]">
            <input type="checkbox" checked={form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} data-testid="editor-active" />
            Active (visible to the assignment queue)
          </label>
          <div className="flex gap-2">
            <button type="button" onClick={handleSubmit} disabled={saving} data-testid="editor-form-submit" className="inline-flex items-center gap-1.5 rounded-sm bg-primary px-3 py-1.5 text-[12px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
              {editingId ? 'Update' : 'Add to roster'}
            </button>
            <button type="button" onClick={() => setFormOpen(false)} data-testid="editor-form-cancel" className="inline-flex items-center gap-1.5 rounded-sm border border-border px-3 py-1.5 text-[12px] hover:bg-secondary">
              <X className="h-3.5 w-3.5" /> Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function EditorialRatesPanel() {
  const [tab, setTab] = useState('rates');
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchJSON(`${API}/admin/editorial/rates`)
      .then(d => { setConfig(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-8 text-[12px] text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading rate configuration…
      </div>
    );
  }

  return (
    <section className="space-y-4 p-4" data-testid="editorial-rates-panel">
      <div>
        <h2 className="text-base font-semibold">Human Editorial Rate Table</h2>
        <p className="text-[11.5px] text-muted-foreground mt-0.5">
          Phase 1 — Rate configuration and editor roster. Author-facing quote tool arrives in Phase 2.
        </p>
      </div>

      {/* Tab switcher */}
      <div className="flex gap-1 rounded-md border border-border bg-muted p-1 w-fit">
        {[['rates', 'Rate Table'], ['roster', 'Editor Roster']].map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            data-testid={`editorial-tab-${key}`}
            className={`rounded px-3 py-1.5 text-[12px] font-medium transition-colors ${tab === key ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'rates' && <RateTableTab config={config} onSaved={setConfig} />}
      {tab === 'roster' && <EditorRosterTab />}
    </section>
  );
}
